import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import { activeTeamMembersColumns, archivedTeamMembersColumns, IMemberRow } from '../Columns';
import { ICoachGroupMembers, Role } from '../../../redux/coachGroup/coachGroupSlice';
import MemberTable, { CellClickProps } from '../MemberTable';
import { formatRoleForDisplay } from '../../../utils/utils';
import { GET_COACH_GROUP_MEMBERS } from '../../../service/graphql/coachGroupQuery';
import { PaginationModelChangeProps } from '../../ClientTable/ClientTable';
import ClientDropdownMenu from '../TeamMemberDropDownMenu';
import { UserPermissionType } from '../../../types/commonTypes';
import createPermissionCheckers from '../../../permissions-validation/createPermissionCheckers';
import { MANAGE_TEAM_ROUTE, TEAM_CLIENTS_ROUTE } from '../../../utils/routes';

export interface ITeamMembersTableProps {
  isArchived: boolean;
}

const TeamMembersTable = ({ isArchived }: ITeamMembersTableProps) => {
  const [getCoachGroup, { loading, data: coachGroup, called }] =
    useLazyQuery<{ getCoachGroupMembers: ICoachGroupMembers }>(GET_COACH_GROUP_MEMBERS);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const coach = useAppSelector((state) => state.coach.coach);

  const [members, setMembers] = useState<IMemberRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const onPageChange = (event: PaginationModelChangeProps) => {
    setPageSize(event.pageSize);
    setCurrentPage(event.page);
  };

  const fetchTeamMembers = () => {
    if (coach) {
      const payload = {
        archived: isArchived,
        take: +pageSize,
        skip: +pageSize * currentPage,
        filterText: searchTerm,
      };
      getCoachGroup({ variables: payload, fetchPolicy: 'network-only' });
    }
  };

  useEffect(() => {
    setCurrentPage(0);
    setSearchTerm('');
    fetchTeamMembers();
  }, [isArchived]);

  useEffect(() => {
    const debounceFetch = setTimeout(() => {
      fetchTeamMembers();
    }, 500);

    return () => clearTimeout(debounceFetch);
  }, [coach.id, pageSize, currentPage, searchTerm]);

  const handleChangeSearchTerm = (newValue: string) => {
    setSearchTerm(newValue);
  };

  const updateRows = () => {
    const teamMembers = coachGroup?.getCoachGroupMembers?.coaches;
    const rows: IMemberRow[] = [];

    if (teamMembers) {
      const mappedMembers = teamMembers.map((member) => ({
        id: member.id,
        avatar: member.profilePic,
        lastActivity: '',
        name: `${member.firstName} ${member.lastName}`,
        firstName: member.firstName,
        lastName: member.lastName,
        profilePic: member.profilePic,
        email: member.email,
        profilePicture: member.profilePic,
        isActive: member.isActive,
        role: member.isPrincipalCoach
          ? formatRoleForDisplay(Role.PRINCIPAL_COACH)
          : formatRoleForDisplay(member.role),
        tp: member.tp,
      }));
      rows.push(...mappedMembers);
    }
    setMembers(rows);
  };

  useEffect(() => {
    if (called && !loading) setTotalCount(coachGroup?.getCoachGroupMembers?.totalCount || 0);
    updateRows();
  }, [called, loading, coachGroup]);

  const handleOnCellClick = (params: CellClickProps) => {
    if (
      coach &&
      !isArchived &&
      activeTeamMembersColumns.some((column) => column.field === params.field) &&
      (params.row.id === coach.id || coach.isPrincipalCoach) &&
      checkPermissions([UserPermissionType.CORE_MODULE_GET_CLIENTS])
    ) {
      navigate(TEAM_CLIENTS_ROUTE.replace(':id', params.row.id), {
        state: {
          selectedCoachId: params.row.id,
          firstName: params.row.firstName,
          lastName: params.row.lastName,
          profilePic: params.row.avatar,
        },
      });
    }
  };

  return (
    <MemberTable
      handleOnCellClick={handleOnCellClick}
      onAddSuccess={fetchTeamMembers}
      rows={members?.length && !loading ? members : []}
      rowCount={totalCount}
      loading={loading}
      columns={
        isArchived || !coach.isPrincipalCoach
          ? archivedTeamMembersColumns
          : [
              ...activeTeamMembersColumns,
              {
                field: 'actions',
                headerName: '',
                width: 100,
                disableColumnMenu: true,
                sortable: false,
                flex: 1,
                renderCell: (params) => (
                  <ClientDropdownMenu row={params.row} onSuccess={fetchTeamMembers} />
                ),
              },
            ]
      }
      handleOnPaginationModelChange={onPageChange}
      handleOnChangeSearchTerm={handleChangeSearchTerm}
      searchTerm={searchTerm}
      pageSize={pageSize}
      page={currentPage}
      addExtraBottomMargin
    />
  );
};

export default TeamMembersTable;
